import {
  Business,
  CalendarMonth,
  Close,
  ConnectWithoutContact,
  Diversity3,
  Groups,
  HelpOutline,
  Lock,
  ReceiptLong,
  RequestQuote,
  Settings,
  ShareLocation,
  SpaceDashboard,
  Storefront,
  ViewKanbanOutlined,
  WorkHistory,
} from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import { colors, alphaVar } from "../../../ui/colors";

type MinimizedModalTabProps = {
  label: string;
  modalKey?: string;
  stacked?: boolean;
  showClose?: boolean;
  mutedLabel?: boolean;
  onRestore: () => void;
  onClose: () => void;
};

const getModalTabIcon = (modalKey?: string) => {
  const key = modalKey ?? "";

  if (/JobCard-|Dashboard-Jobs|CalendarEvent-JobCard|Timeline-Job|Jobs-Calendar/.test(key)) return WorkHistory;
  if (/Calendar|Visit|Event/.test(key)) return CalendarMonth;
  if (/Workforce|Workforces/.test(key)) return Groups;
  if (/Client|Clients/.test(key)) return Diversity3;
  if (/Invoice/.test(key)) return ReceiptLong;
  if (/Quote/.test(key)) return RequestQuote;
  if (/FlowDo|Flow-Do|Flow Do/.test(key)) return ViewKanbanOutlined;
  if (/Connection|FindAExpert/.test(key)) return ConnectWithoutContact;
  if (/Vendor|Provider|Supplie|Supply/.test(key)) return Storefront;
  if (/Coverage/.test(key)) return ShareLocation;
  if (/Password|Vault/.test(key)) return Lock;
  if (/Account|Settings|TimeZone|Manage/.test(key)) return Settings;
  if (/Help|Terms|Privacy/.test(key)) return HelpOutline;
  if (/Company|Business|Organization/.test(key)) return Business;

  return SpaceDashboard;
};

export const MinimizedModalTab = ({
  label,
  modalKey,
  stacked = false,
  showClose = false,
  mutedLabel = true,
  onRestore,
  onClose,
}: MinimizedModalTabProps) => {
  const TabIcon = getModalTabIcon(modalKey);

  return (
    <Box
      className="minimized-modal-tab-card"
      role="button"
      tabIndex={0}
      onClick={onRestore}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onRestore();
        }
      }}
      sx={{
        height: 24,
        width: 136,
        minWidth: 136,
        maxWidth: 136,
        display: "grid",
        gridTemplateColumns: "14px minmax(0, 1fr) 18px",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        position: "relative",
        overflow: "hidden",
        borderRadius: "8px",
        border: `1px solid ${colors.gray[100]}`,
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
        color: colors.gray.base,
        cursor: "pointer",
        boxShadow: stacked ? "-6px 0 12px rgba(15, 23, 42, 0.12), 0 2px 6px rgba(0,0,0,0.06)" : "0 2px 6px rgba(0,0,0,0.06)",
        transition: "background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
        "&:hover": {
          bgcolor: colors.warmWhite?.soft || "var(--fx-surface-base, #FFFFFF)",
          borderColor: colors.orange[200],
          boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
        },
        "&:focus-visible": {
          outline: `2px solid ${alphaVar(colors.orange.base, 0.45)}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box
        sx={{
          width: 14,
          height: 14,
          display: "grid",
          placeItems: "center",
        }}
      >
        <TabIcon sx={{ fontSize: 12, color: alphaVar(colors.gray.base, 0.62) }} />
      </Box>

      <Typography
        className="minimized-modal-tab-label"
        noWrap
        sx={{
          fontSize: 10.5,
          fontWeight: 800,
          lineHeight: 1,
          color: colors.gray[700] || colors.gray.base,
          opacity: mutedLabel ? 0.68 : 1,
          transition: "opacity 140ms ease",
        }}
      >
        {label}
      </Typography>

      <IconButton
        className="minimized-modal-tab-close"
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          onClose();
        }}
        sx={{
          width: 16,
          height: 16,
          p: 0,
          opacity: showClose ? 1 : 0,
          pointerEvents: showClose ? "auto" : "none",
          transition: "opacity 140ms ease, color 140ms ease, background-color 140ms ease",
          color: alphaVar(colors.gray.base, 0.48),
          "&:hover": {
            bgcolor: alphaVar(colors.orange.base, 0.08),
            color: colors.orange.base,
          },
        }}
      >
        <Close sx={{ fontSize: 12 }} />
      </IconButton>
    </Box>
  );
};
